<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import ShortCutsUtil from "@/utils/common/ShortCutsUtil.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import ActionHandler from "@/utils/moreMenu/ActionHandler.ts";
import EditViewPerformer from "@/utils/presenters/EditViewPerformer.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {Dialog} from "quasar";
import ShowFieldInfoDialog from "@/components/dialogs/ShowFieldInfoDialog.vue";
import EditUIElementPropertiesBtn from "@/components/EditView/EditUIElementPropertiesBtn.vue";
import ListViewPerformer from "@/utils/presenters/ListViewPerformer.ts";
import ActionPerformer from "@/utils/moreMenu/ActionPerformer.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import {ICellRendererParams} from "ag-grid-community";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import GUIActionPlaceholder = namaTypes.GUIActionPlaceholder;
import GUIActionInfo = namaTypes.GUIActionInfo;

const props = defineProps<{ presenter: WidgetsPresenter, action: GUIActionPlaceholder, params?: ICellRendererParams }>();
const {compactMode, showFieldInfoActive} = storeToRefs(useNamaFlagsStore());
const presenter = props.presenter!;

function getPerformer(): ActionPerformer {
  if (presenter instanceof EditPresenter) {
    return new EditViewPerformer(presenter);
  } else {
    const listViewPerformer = new ListViewPerformer(presenter as ListPresenter);
    const rowData = props.params?.data;
    const externalContextFields:Record<string, any> = {namaRowIndex: rowData['namaRowIndex']};
    const metaData = presenter.fetchMetadata();
    const actionInfo: GUIActionInfo = metaData.entityActions.actions.find(a => a.id === props.action.id);
    if (actionInfo) {
      const contextFieldIds = actionInfo.contextFields;
      for (let contextFieldId of contextFieldIds) {
        if (contextFieldId in rowData)
          externalContextFields[contextFieldId] = rowData[contextFieldId];
      }
    }
    let id: string
    const runCustomActionOn = props.action.customization.runCustomActionOn;
    if (ObjectChecker.isNotEmptyOrNull(runCustomActionOn))
      id = rowData?.[runCustomActionOn]?.[CommonFieldIds.ID]
    else
      id = rowData[CommonFieldIds.ID]
    externalContextFields.id = id;
    listViewPerformer.getSearchContextProvider().externalContextFields = externalContextFields;
    return listViewPerformer
  }
}

const actionHandler = new ActionHandler(props.action, presenter.fetchMetadata(), presenter.entityType, getPerformer());

function showActionInfoDialog(e: MouseEvent) {
  if (!showFieldInfoActive.value)
    return;
  e.preventDefault()
  Dialog.create({
    component: ShowFieldInfoDialog,
    componentProps: {
      action: props.action,
      entityType: presenter.entityType
    }
  });
}
</script>

<template>
  <div class="tw-flex tw-items-center tw-gap-1 tw-max-h-full tw-w-full">
    <EditUIElementPropertiesBtn :actionInfo="action" :entity-type="presenter.entityType"/>
    <q-btn
      @contextmenu="showActionInfoDialog"
      filled
      no-caps
      :dense="compactMode"
      :title="ShortCutsUtil.addShortCutToTitle('', action.id!)"
      :data-action-id="action.id"
      class="tw-text-white tw-bg-primary-light dark:tw-bg-primary-dark tw-text-base tw-font-normal tw-rounded tw-max-h-full compact:tw-py-1 ultra:tw-py-0"
      @click.stop="actionHandler.performAction();">
      <div class="tw-flex tw-items-center tw-gap-1.5">
        <NamaIcon default-icon="nm-actions" :presenter="presenter" :block="action" class="active-white tw-size-4 tw-text-[16px]"/>
        <span v-text="Translator.translateTitledId(presenter.entityType, props.action) || 'No Title Assigned'"/>
      </div>
    </q-btn>
  </div>
</template>

